import { useState } from 'react';
import type { MessageDetail, PartStat } from 'zitimail-shared-types';

import { calendar } from '../api/calendar';

const ANSWERED: Record<PartStat, string> = {
  ACCEPTED: 'accepted',
  DECLINED: 'declined',
  TENTATIVE: 'said maybe to',
  'NEEDS-ACTION': 'has not answered',
};

function when(inv: NonNullable<MessageDetail['invitation']>): string {
  if (inv.allDay) {
    return new Date(inv.start).toLocaleDateString([], {
      timeZone: 'UTC',
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }
  const start = new Date(inv.start);
  const end = new Date(inv.end);
  return `${start.toLocaleString([], { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })} – ${end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

/** An invitation, reply or cancellation in the reader, answerable in place. */
export function Invitation({
  message,
  onChanged,
}: {
  message: MessageDetail;
  onChanged: () => void;
}): JSX.Element | null {
  const inv = message.invitation;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!inv) {
    return null;
  }
  const answer = (a: 'accept' | 'tentative' | 'decline') => {
    setBusy(true);
    setError(null);
    calendar
      .rsvp(message.folder, message.uid, a)
      .then(onChanged)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Not answered.'))
      .finally(() => setBusy(false));
  };
  return (
    <div
      className={`invitation ${inv.method.toLowerCase()}`}
      role="group"
      aria-label="Calendar invitation"
    >
      <strong>
        {inv.method === 'CANCEL' ? 'Cancelled: ' : ''}
        {inv.title}
      </strong>
      <span>{when(inv)}</span>
      {inv.location && <span>{inv.location}</span>}
      {inv.method === 'REQUEST' && (
        <>
          {inv.organizer && <span className="muted">From {inv.organizer}</span>}
          <div className="compose-foot">
            {(['accept', 'tentative', 'decline'] as const).map((a) => {
              const status = { accept: 'ACCEPTED', tentative: 'TENTATIVE', decline: 'DECLINED' }[a];
              return (
                <button
                  type="button"
                  key={a}
                  className={`btn ${inv.myStatus === status ? '' : 'ghost'}`}
                  style={{ width: 'auto' }}
                  disabled={busy}
                  aria-pressed={inv.myStatus === status}
                  onClick={() => answer(a)}
                >
                  {{ accept: 'Accept', tentative: 'Maybe', decline: 'Decline' }[a]}
                </button>
              );
            })}
            {inv.inCalendar && <span className="muted">In your calendar</span>}
          </div>
        </>
      )}
      {inv.method === 'REPLY' && inv.from && (
        <>
          <span>
            {inv.from.name ?? inv.from.email} {ANSWERED[inv.from.partstat]} this invitation.
            {inv.inCalendar && inv.applied ? ' Your calendar shows it.' : ''}
          </span>
          {inv.inCalendar && !inv.applied && (
            <div className="compose-foot">
              <button
                type="button"
                className="btn"
                style={{ width: 'auto' }}
                disabled={busy}
                onClick={() => {
                  setBusy(true);
                  setError(null);
                  calendar
                    .applyReply(message.folder, message.uid)
                    .then(onChanged)
                    .catch((e: unknown) =>
                      setError(e instanceof Error ? e.message : 'Not updated.'),
                    )
                    .finally(() => setBusy(false));
                }}
              >
                Update my calendar
              </button>
            </div>
          )}
        </>
      )}
      {inv.method === 'CANCEL' && (
        <span className="muted">The organizer cancelled this event.</span>
      )}
      {error && (
        <span className="adm-error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
